import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import authService from "../../api-authorization/AuthorizeService";
import LoadingSpinner from "../../components/LoadingSpinner";
import RequestOverlay from "../../components/RequestOverlay";
import styles from "./AdminConfigCommands.module.css";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Divider,
  Box,
  TextField,
  Stack,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Slider,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

const PWM_MODES = [
  { label: 'Spot Plain', key: 'Spot Plain', value: 0, sensitivityType: 'Sensitivity' },
  { label: 'Spot Spraying', key: 'Spot Spraying', value: 1, sensitivityType: 'Sensitivity' },
  { label: 'Broadcast', key: 'Broadcast', value: 2, sensitivityType: 'Broadcast' },
  { label: 'Dual Rate', key: 'Dual Rate', value: 3, sensitivityType: 'Sensitivity' },
  { label: 'SiC Plain (not available in all hardware)', key: 'SiC Plain', value: 4, sensitivityType: 'GOG' },
  { label: 'SiC Spot (not available in all hardware)', key: 'SiC Spot', value: 5, sensitivityType: 'GOG' },
  { label: 'SiC Broadcast (not available in all hardware)', key: 'SiC Broadcast', value: 6, sensitivityType: 'GOG' },
  { label: 'SiC Dual Rate (not available in all hardware)', key: 'SiC Dual Rate', value: 7, sensitivityType: 'GOG' },
];

const AdminConfigCommands = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tractorId = searchParams.get('tractorId');

  const [tractor, setTractor] = useState(null);
  const [diagnosticsReportStatus, setDiagnosticsReportStatus] = useState({});
  const [, setLoading] = useState(false);
  const code = useRef(null);

  const [diagnosticsReportStatusError, setDiagnosticsReportStatusError] =
    useState("");

  const [diagnosticsReportStatusLoading, setDiagnosticsReportStatusLoading] =
    useState(false);

  const diagnosticsReportStatusIntervalRef = useRef();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, []);

  // Fetch tractor details
  useEffect(() => {
    const fetchTractor = async () => {
      if (!tractorId) return;

      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/all`,
          {
            headers: !token ? {} : { Authorization: `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const tractors = await response.json();
          const foundTractor = tractors.find(t => t.id === parseInt(tractorId));
          setTractor(foundTractor);
        }
      } catch (error) {
        console.error("Error fetching tractor:", error);
      }
    };

    fetchTractor();
  }, [tractorId]);

  useEffect(() => {
    if (diagnosticsReportStatus.completed) {
      clearInterval(diagnosticsReportStatusIntervalRef.current);
      setDiagnosticsReportStatusLoading(false);
    }
  }, [diagnosticsReportStatus]);

  useEffect(() => {
    return () => {
      clearInterval(diagnosticsReportStatusIntervalRef.current);
    };
  }, []);

  const handleReportStatus = async () => {
    if (!tractorId) return;
    setDiagnosticsReportStatusLoading(true);
    setDiagnosticsReportStatusError("");
    const token = await authService.getAccessToken();

    fetch(`${process.env.REACT_APP_BASE_URL}/api/diagnostics/report-status`, {
      method: "POST",
      headers: !token
        ? {}
        : {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      body: JSON.stringify({
        tractorId: tractorId,
      }),
    })
      .then((res) => res.json())
      .then(async (data) => {
        toast.success("Command sent to device - Waiting on response");
        code.current = data.code;

        diagnosticsReportStatusIntervalRef.current = setInterval(async () => {
          try {
            const diagnostics = await fetch(
              `${process.env.REACT_APP_BASE_URL}/api/diagnostics/report-status?code=${data.code}`
            );
            const jsonData = await diagnostics.json();
            setDiagnosticsReportStatus(jsonData);
          } catch (error) {
            clearInterval(diagnosticsReportStatusIntervalRef.current);
            setDiagnosticsReportStatusLoading(false);
            setDiagnosticsReportStatusError("There was an error.");
          }
        }, 2000);
      })
      .catch((err) => {
        console.log("err", err);
        toast.error("error");
        setDiagnosticsReportStatusLoading(false);
      });
  };

  // Heartbeats button loading state
  const [heartbeatsLoading, setHeartbeatsLoading] = useState(false);
  const [timeOutMin, setTimeOutMin] = useState(null);
  const [heartbeatsStatus, setHeartbeatsStatus] = useState(null);
  const [heartbeatsStatusError, setHeartbeatsStatusError] = useState("");
  const heartbeatsStatusIntervalRef = useRef();
  const heartbeatsCodeRef = useRef();

  // Handler for Enable Heartbeats button
  const handleEnableHeartbeats = async () => {
    if (!tractorId || !timeOutMin) return;
    setHeartbeatsLoading(true);
    setHeartbeatsStatus(null);
    setHeartbeatsStatusError("");

    try {
      const token = await authService.getAccessToken();
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/diagnostics/force-heartbeats`,
        {
          method: "POST",
          headers: !token
            ? {}
            : {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          body: JSON.stringify({
            tractorId: tractorId,
            timeOutMin: Number(timeOutMin),
          }),
        }
      );
      const data = await res.json();
      if (res.ok && data.code) {
        toast.success("Command sent to device - Waiting on response");
        heartbeatsCodeRef.current = data.code;

        // Start polling for status
        heartbeatsStatusIntervalRef.current = setInterval(async () => {
          try {
            const statusRes = await fetch(
              `${process.env.REACT_APP_BASE_URL}/api/diagnostics/force-heartbeats?code=${data.code}`
            );
            const statusData = await statusRes.json();
            setHeartbeatsStatus(statusData);

            // If completed, stop polling and hide spinner
            if (statusData.completed) {
              clearInterval(heartbeatsStatusIntervalRef.current);
              setHeartbeatsLoading(false);
            }
          } catch (err) {
            clearInterval(heartbeatsStatusIntervalRef.current);
            setHeartbeatsStatusError("Error fetching heartbeats status.");
            setHeartbeatsLoading(false);
          }
        }, 5000);
      } else {
        setHeartbeatsStatusError("Failed to enable heartbeats.");
        setHeartbeatsLoading(false);
      }
    } catch (err) {
      setHeartbeatsStatusError("Error enabling heartbeats.");
      setHeartbeatsLoading(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      clearInterval(heartbeatsStatusIntervalRef.current);
    };
  }, []);

  // Weed-It Settings state
  const [weeditOptions, setWeeditOptions] = useState(null);
  const [pwmMode, setPwmMode] = useState(0);
  const [sensitivity, setSensitivity] = useState(1);
  const [marginPreset, setMarginPreset] = useState(1);
  const [spotSprayingPercent, setSpotSprayingPercent] = useState(50);
  const [coverSprayingPercent, setCoverSprayingPercent] = useState(1);
  const [optimumSpeedMph, setOptimumSpeedMph] = useState(5);
  const [nominalPressurePsi, setNominalPressurePsi] = useState(50);
  const [weeditSettingsLoading, setWeeditSettingsLoading] = useState(false);
  const [weeditSettingsError, setWeeditSettingsError] = useState('');
  const [weeditSettingsStatus, setWeeditSettingsStatus] = useState(null);
  const [rebootConfirmOpen, setRebootConfirmOpen] = useState(false);
  const weeditSettingsIntervalRef = useRef();
  const seedValuesRef = useRef({
    pwmMode: 0,
    sensitivity: 1,
    marginPreset: 1,
    spotSprayingPercent: 50,
    coverSprayingPercent: 1,
    optimumSpeedMph: 5,
    nominalPressurePsi: 50,
  });

  // Fetch weedit options for slider bounds
  useEffect(() => {
    const fetchWeeditOptions = async () => {
      if (!tractorId) return;
      try {
        const token = await authService.getAccessToken();
        const devicesResponse = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/devices/all-by-tractor?tractorId=${tractorId}`,
          { headers: !token ? {} : { Authorization: `Bearer ${token}` } }
        );
        if (!devicesResponse.ok) return;
        const devices = await devicesResponse.json();
        const serialNumber = devices?.[0]?.serialNumber;
        if (!serialNumber) return;

        const [optionsResponse, heartbeatsResponse] = await Promise.all([
          fetch(
            `${process.env.REACT_APP_BASE_URL}/api/devices/weedit-options?deviceSerialNumber=${serialNumber}`,
            { headers: !token ? {} : { Authorization: `Bearer ${token}` } }
          ),
          fetch(
            `${process.env.REACT_APP_BASE_URL}/api/admin/devices/last-weedit-heartbeat?deviceSerialNumber=${serialNumber}`,
            { headers: !token ? {} : { Authorization: `Bearer ${token}` } }
          ),
        ]);
        
        let minCoverage = 1;
        if (optionsResponse.ok) {
          const data = await optionsResponse.json();
          setWeeditOptions(data);
          if (data.minCoveragePercent > 0) minCoverage = data.minCoveragePercent;
        }

        let heartbeatSeeded = false;
        if (heartbeatsResponse.ok) {
          const h = await heartbeatsResponse.json();
          if (h) {
            heartbeatSeeded = true;
            const matchedMode = PWM_MODES.find(m => m.key.toLowerCase() === h.weedItPwmMode?.trim().toLowerCase());

            const seeded = {
              pwmMode: matchedMode !== undefined ? matchedMode.value : seedValuesRef.current.pwmMode,
              sensitivity: matchedMode?.sensitivityType === 'Broadcast' ? 1 : (h.weedItSensitivity > 0 ? h.weedItSensitivity : seedValuesRef.current.sensitivity),
              marginPreset: h.weedItMarginPreset > 0 ? h.weedItMarginPreset : seedValuesRef.current.marginPreset,
              spotSprayingPercent: h.weedItNozzleSpotPercent > 0 ? Math.round(h.weedItNozzleSpotPercent * 100) : seedValuesRef.current.spotSprayingPercent,
              coverSprayingPercent: h.weedItNozzleCoverPercent * 100 > 0 ? Math.max(Math.round(h.weedItNozzleCoverPercent * 100), minCoverage) : minCoverage,
              optimumSpeedMph: h.weedItNozzleOptimumSpeedMph > 0 ? Math.round(h.weedItNozzleOptimumSpeedMph) : seedValuesRef.current.optimumSpeedMph,
              nominalPressurePsi: h.weedItNozzleNominalPressurePsi > 0 ? Math.round(h.weedItNozzleNominalPressurePsi) : seedValuesRef.current.nominalPressurePsi,
            };

            setPwmMode(seeded.pwmMode);
            setSensitivity(seeded.sensitivity);
            setMarginPreset(seeded.marginPreset);
            setSpotSprayingPercent(seeded.spotSprayingPercent);
            setCoverSprayingPercent(seeded.coverSprayingPercent);
            setOptimumSpeedMph(seeded.optimumSpeedMph);
            setNominalPressurePsi(seeded.nominalPressurePsi);

            seedValuesRef.current = seeded;
          }
        }

        if (!heartbeatSeeded && minCoverage > 1) {
          setCoverSprayingPercent(minCoverage);
          seedValuesRef.current = { ...seedValuesRef.current, coverSprayingPercent: minCoverage };
        }
      } catch (error) {
        console.error('Error fetching weedit options:', error);
      }
    };
    fetchWeeditOptions();
  }, [tractorId]);

  const selectedMode = PWM_MODES.find(m => m.value === pwmMode);
  const sensitivityMax = selectedMode?.sensitivityType === 'GOG'
    ? (weeditOptions?.maxGogSensitivityPresets || 10)
    : (weeditOptions?.maxSensitivityPresets || 10);
  const coverSprayingMin = weeditOptions?.minCoveragePercent || 1;
  const isBroadcastMode = selectedMode?.sensitivityType === 'Broadcast';
  const hasWeeditChanges =
    pwmMode !== seedValuesRef.current.pwmMode ||
    sensitivity !== seedValuesRef.current.sensitivity ||
    marginPreset !== seedValuesRef.current.marginPreset ||
    spotSprayingPercent !== seedValuesRef.current.spotSprayingPercent ||
    Math.max(coverSprayingPercent, coverSprayingMin) !== seedValuesRef.current.coverSprayingPercent ||
    optimumSpeedMph !== seedValuesRef.current.optimumSpeedMph ||
    nominalPressurePsi !== seedValuesRef.current.nominalPressurePsi;

  const handlePwmModeChange = (newMode) => {
    const newModeEntry = PWM_MODES.find(m => m.value === newMode);
    const newMax = newModeEntry?.sensitivityType === 'GOG'
      ? (weeditOptions?.maxGogSensitivityPresets || 10)
      : (weeditOptions?.maxSensitivityPresets || 10);
    setPwmMode(newMode);
    if (newModeEntry?.sensitivityType === 'Broadcast') {
      setSensitivity(1);
    } else {
      setSensitivity(prev => Math.min(prev, newMax));
    }
  };

  useEffect(() => {
    return () => {
      clearInterval(weeditSettingsIntervalRef.current);
    };
  }, []);

  const doSendWeeditSettings = async () => {
    if (!tractorId) return;
    setWeeditSettingsLoading(true);
    setWeeditSettingsError('');
    setWeeditSettingsStatus(null);
    try {
      const token = await authService.getAccessToken();
      const seed = seedValuesRef.current;
      const effectiveCoverSprayingPercent = Math.max(coverSprayingPercent, coverSprayingMin);
      const newSeed = {
        pwmMode,
        sensitivity,
        marginPreset,
        spotSprayingPercent,
        coverSprayingPercent: effectiveCoverSprayingPercent,
        optimumSpeedMph,
        nominalPressurePsi,
      };
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/diagnostics/weedit-settings`,
        {
          method: 'POST',
          headers: !token ? {} : { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tractorId: Number(tractorId),
            pwmMode: pwmMode !== seed.pwmMode ? pwmMode : null,
            sensitivity: sensitivity !== seed.sensitivity ? sensitivity : null,
            marginPreset: marginPreset !== seed.marginPreset ? marginPreset : null,
            spotSprayingPercent: spotSprayingPercent !== seed.spotSprayingPercent ? spotSprayingPercent : null,
            coverSprayingPercent: effectiveCoverSprayingPercent !== seed.coverSprayingPercent ? effectiveCoverSprayingPercent : null,
            optimumSpeedMph: optimumSpeedMph !== seed.optimumSpeedMph ? optimumSpeedMph : null,
            nominalPressurePsi: nominalPressurePsi !== seed.nominalPressurePsi ? nominalPressurePsi : null,
          }),
        }
      );
      const data = await response.json();
      if (response.ok && data.code) {
        toast.success('Command sent to device - Waiting on response');

        weeditSettingsIntervalRef.current = setInterval(async () => {
          try {
            const statusRes = await fetch(
              `${process.env.REACT_APP_BASE_URL}/api/diagnostics/weedit-settings?code=${data.code}`
            );
            const statusData = await statusRes.json();
            setWeeditSettingsStatus(statusData);

            if (statusData.completed) {
              clearInterval(weeditSettingsIntervalRef.current);
              seedValuesRef.current = newSeed;
              setWeeditSettingsLoading(false);
              checkForTodaysRuns();
            }
          } catch (err) {
            clearInterval(weeditSettingsIntervalRef.current);
            setWeeditSettingsError('Error fetching weedit settings status.');
            setWeeditSettingsLoading(false);
          }
        }, 5000);
      } else {
        setWeeditSettingsError(`${response.status} ${response.statusText}`);
        setWeeditSettingsLoading(false);
      }
    } catch (err) {
      setWeeditSettingsError('Error: ' + err);
      setWeeditSettingsLoading(false);
    }
  };

  const handleSetWeeditSettings = () => {
    if (!tractorId) return;
    const seed = seedValuesRef.current;
    const effectiveCoverSprayingPercent = Math.max(coverSprayingPercent, coverSprayingMin);
    const rebootTriggered =
      pwmMode !== seed.pwmMode ||
      spotSprayingPercent !== seed.spotSprayingPercent ||
      effectiveCoverSprayingPercent !== seed.coverSprayingPercent ||
      optimumSpeedMph !== seed.optimumSpeedMph ||
      nominalPressurePsi !== seed.nominalPressurePsi;

    if (rebootTriggered) {
      setRebootConfirmOpen(true);
    } else {
      doSendWeeditSettings();
    }
  };

  // Create Job dialog state
  const [createJobDialogOpen, setCreateJobDialogOpen] = useState(false);
  const [createJobName, setCreateJobName] = useState('');
  const [createJobError, setCreateJobError] = useState('');
  const [todaysRuns, setTodaysRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [createJobLoading, setCreateJobLoading] = useState(false);

  const checkForTodaysRuns = async () => {
    try {
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/runs/by-tractor?tractorId=${tractorId}`,
        { headers: !token ? {} : { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return;
      const runs = await response.json();
      const now = new Date();
      const todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
      const filtered = runs.filter(r => {
        const d = new Date(r.startTimeTractorTimezone);
        const dateStr = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        return dateStr === todayStr;
      });
      if (filtered.length > 0) {
        setTodaysRuns(filtered);
        setSelectedRunId(filtered[0].id);
        setCreateJobName('');
        setCreateJobError('');
        setCreateJobDialogOpen(true);
      }
    } catch (err) {
      console.error('Error checking for today\'s runs:', err);
    }
  };

  const handleCreateJob = async () => {
    const trimmedJobName = createJobName.trim();
    if (!selectedRunId || !trimmedJobName) return;

    setCreateJobLoading(true);
    setCreateJobError('');

    try {
      const token = await authService.getAccessToken();
      const headers = !token ? {} : { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      const addResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/jobs`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ runId: selectedRunId, name: trimmedJobName }),
        }
      );

      let responseData = null;
      try {
        responseData = await addResponse.json();
      } catch {
        responseData = null;
      }

      if (addResponse.ok) {
        toast.success('Job created successfully.');
        setCreateJobDialogOpen(false);
      } else if (addResponse.status === 409) {
        setCreateJobError(responseData?.message || `${trimmedJobName} name is already exists.`);
      } else {
        toast.error(responseData?.message || 'Failed to create job.');
      }

      setCreateJobLoading(false);
    } catch (err) {
      toast.error('Error creating job: ' + err.message);
      setCreateJobLoading(false);
    }
  };

  // Reboot dialog state
  const [rebootDialogOpen, setRebootDialogOpen] = useState(false);
  const [rebootLoading, setRebootLoading] = useState(false);

  const handleRebootTractor = async () => {
    setRebootLoading(true);
    try {
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/diagnostics/reboot-tractor`,
        {
          method: 'POST',
          headers: !token ? {} : { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ tractorId: Number(tractorId) }),
        }
      );
      const data = await response.json();
      setRebootLoading(false);
      if (response.ok) {
        toast.success('Reboot command sent - Tractor will restart shortly.');
        setRebootDialogOpen(false);
      } else {
        setRebootDialogOpen(false);
        toast.error(data.message || 'Failed to send reboot command.');
      }
    } catch (err) {
      setRebootLoading(false);
      setRebootDialogOpen(false);
      toast.error('Error: ' + err.message);
    }
  };

  return (
    <Box
      className={styles.container}
      sx={{
        pb: { xs: 3, sm: 2 },
        width: { xs: '100%', sm: 900 },
        maxWidth: '100%',
        mx: 'auto'
      }}
    >
      <RequestOverlay
        isLoading={!tractorId}
        id="loading-overlay"
        errorTitle="Missing required parameters."
        errorSubtitle="Client ID and Tractor ID are required."
      />

      <Typography variant="h4" sx={{ pt: { xs: 2, sm: 0 }, pb: 1 }}>
        {tractor?.name}
      </Typography>
      <Typography variant="h5" sx={{ pb: 3 }}>
        Device Commands
      </Typography>

      {/* Report Status Section */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, width: '100%' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Report Status
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Stack spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleReportStatus}
            disabled={diagnosticsReportStatusLoading || !tractorId}
            fullWidth={isMobile}
            sx={{ minWidth: 200 }}
          >
            {diagnosticsReportStatusLoading ? <LoadingSpinner /> : "Report Status"}
          </Button>

          {diagnosticsReportStatusError && (
            <Typography color="error" sx={{ textAlign: 'center' }}>
              {diagnosticsReportStatusError}
            </Typography>
          )}

          {diagnosticsReportStatus?.data?.length ? (
            <TableContainer sx={{ mt: 2, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, background: "#f5f5f5", whiteSpace: 'nowrap' }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 600, background: "#f5f5f5", whiteSpace: 'nowrap' }}>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {diagnosticsReportStatus.data.map((i) => (
                    <TableRow key={i.serialNumber || i.deviceId || Math.random()}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{i.serialNumber}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word', maxWidth: { xs: 200, sm: 400 } }}>{i.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Stack>
      </Paper>

      {/* Enable Heartbeats Section */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, width: '100%' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Enable Heartbeats
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Stack spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <FormControl fullWidth={isMobile} sx={{ width: { xs: '100%', sm: 200 } }}>
            <FormLabel>Number of Minutes</FormLabel>
            <TextField
              type="number"
              value={timeOutMin}
              onChange={e => setTimeOutMin(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              size={isMobile ? "medium" : "small"}
              fullWidth
            />
          </FormControl>

          <Button
            variant="outlined"
            onClick={handleEnableHeartbeats}
            disabled={heartbeatsLoading || !tractorId || !timeOutMin}
            fullWidth={isMobile}
            sx={{ minWidth: 200 }}
          >
            {heartbeatsLoading ? <LoadingSpinner /> : "Enable Heartbeats"}
          </Button>

          {heartbeatsStatusError && (
            <Typography color="error" sx={{ textAlign: 'center' }}>
              {heartbeatsStatusError}
            </Typography>
          )}

          {heartbeatsStatus?.data?.length ? (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, background: "#f5f5f5" }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 600, background: "#f5f5f5" }}>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {heartbeatsStatus.data.map((i) => (
                    <TableRow key={i.serialNumber || i.deviceId || Math.random()}>
                      <TableCell>{i.serialNumber}</TableCell>
                      <TableCell>{i.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Stack>
      </Paper>

      {/* Weed-It Settings Section */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, width: '100%' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Weed-It Settings
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>

          {/* Spray Mode */}
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 0.5 }}>Spray Mode</FormLabel>
            <Select
              value={pwmMode}
              onChange={e => handlePwmModeChange(e.target.value)}
              size="small"
            >
              {PWM_MODES.map(mode => (
                <MenuItem key={mode.value} value={mode.value}>{mode.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sensitivity */}
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 0.5 }}>Sensitivity (1 – {sensitivityMax})</FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Slider
                value={sensitivity}
                min={1}
                max={sensitivityMax}
                step={1}
                valueLabelDisplay="auto"
                disabled={isBroadcastMode}
                onChange={(_, val) => setSensitivity(val)}
                sx={{ mt: 1, flex: 1 }}
              />
              <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'right' }}>{sensitivity}</Typography>
            </Box>
          </FormControl>

          {/* Margin Preset */}
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 0.5 }}>Margin Preset (1 – 4)</FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Slider
                value={marginPreset}
                min={1}
                max={4}
                step={1}
                valueLabelDisplay="auto"
                onChange={(_, val) => setMarginPreset(val)}
                sx={{ mt: 1, flex: 1 }}
              />
              <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'right' }}>{marginPreset}</Typography>
            </Box>
          </FormControl>

          {/* Spot Spraying Percent */}
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 0.5 }}>Spot Spraying Percent (1 – 100)</FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Slider
                value={spotSprayingPercent}
                min={1}
                max={100}
                step={1}
                valueLabelDisplay="auto"
                onChange={(_, val) => setSpotSprayingPercent(val)}
                sx={{ mt: 1, flex: 1 }}
              />
              <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'right' }}>{spotSprayingPercent}%</Typography>
            </Box>
          </FormControl>

          {/* Cover Spraying Percent */}
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 0.5 }}>Cover Spraying Percent ({coverSprayingMin} – 100)</FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Slider
                value={Math.max(coverSprayingPercent, coverSprayingMin)}
                min={coverSprayingMin}
                max={100}
                step={1}
                valueLabelDisplay="auto"
                onChange={(_, val) => setCoverSprayingPercent(val)}
                sx={{ mt: 1, flex: 1 }}
              />
              <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'right' }}>{Math.max(coverSprayingPercent, coverSprayingMin)}%</Typography>
            </Box>
          </FormControl>

          {/* Optimum Speed */}
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 0.5 }}>Optimum Speed (mph) (1 – 15)</FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Slider
                value={optimumSpeedMph}
                min={1}
                max={15}
                step={.1}
                valueLabelDisplay="auto"
                onChange={(_, val) => setOptimumSpeedMph(val)}
                sx={{ mt: 1, flex: 1 }}
              />
              <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'right' }}>{optimumSpeedMph}</Typography>
            </Box>
          </FormControl>

          {/* Nominal Pressure */}
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 0.5 }}>Nominal Pressure (psi) (1 – 100)</FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Slider
                value={nominalPressurePsi}
                min={1}
                max={100}
                step={1}
                valueLabelDisplay="auto"
                onChange={(_, val) => setNominalPressurePsi(val)}
                sx={{ mt: 1, flex: 1 }}
              />
              <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'right' }}>{nominalPressurePsi}</Typography>
            </Box>
          </FormControl>

          <Button
            variant="outlined"
            onClick={handleSetWeeditSettings}
            disabled={weeditSettingsLoading || !tractorId || !hasWeeditChanges}
            fullWidth={isMobile}
            sx={{ minWidth: 200, alignSelf: { xs: 'stretch', sm: 'center' } }}
          >
            {weeditSettingsLoading ? < LoadingSpinner / > : 'Send Settings to Device'}
          </Button>

          {weeditSettingsError && (
            <Typography color="error" sx={{ textAlign: 'center' }}>
              {weeditSettingsError}
            </Typography>
          )}

          {weeditSettingsStatus?.data?.length ? (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, background: "#f5f5f5" }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 600, background: "#f5f5f5" }}>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeditSettingsStatus.data.map((i) => (
                    <TableRow key={i.serialNumber || i.deviceId || Math.random()}>
                      <TableCell>{i.serialNumber}</TableCell>
                      <TableCell>{i.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Stack>
      </Paper>

      {/* Reboot Dialog */}
      <Dialog
        open={rebootDialogOpen}
        onClose={() => setRebootDialogOpen(false)}
        aria-labelledby="reboot-dialog-title"
        aria-describedby="reboot-dialog-description"
      >
        <DialogTitle id="reboot-dialog-title">Reboot Tractor</DialogTitle>
        <DialogContent>
          <DialogContentText id="reboot-dialog-description">
            Are you sure you want to reboot the tractor? This will cause the tractor to restart immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebootDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleRebootTractor}
            color="primary"
            autoFocus
            disabled={rebootLoading}
          >
            {rebootLoading ? <LoadingSpinner size={20} /> : "Reboot"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rebootConfirmOpen} onClose={() => setRebootConfirmOpen(false)}>
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This change will cause Weed-It to reboot. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebootConfirmOpen(false)}>No</Button>
          <Button
            variant="contained"
            onClick={() => {
              setRebootConfirmOpen(false);
              doSendWeeditSettings();
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Job Dialog */}
      <Dialog open={createJobDialogOpen} onClose={() => setCreateJobDialogOpen(false)}>
        <DialogTitle>Create New Job</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Do you want to create a new job?
          </DialogContentText>
          {todaysRuns.length > 1 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel sx={{ mb: 0.5 }}>Select Run</FormLabel>
              <Select
                value={selectedRunId}
                onChange={e => setSelectedRunId(e.target.value)}
                size="small"
              >
                {todaysRuns.map(run => (
                  <MenuItem key={run.id} value={run.id}>
                    Run {run.displayId} — {new Date(run.startTimeUtc).toLocaleTimeString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            autoFocus
            label="Job Name"
            fullWidth
            size="small"
            value={createJobName}
            onChange={e => {
              setCreateJobName(e.target.value);
              setCreateJobError('');
            }}
            error={!!createJobError}
            helperText={createJobError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateJobDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateJob}
            disabled={createJobLoading || !createJobName.trim()}
          >
            {createJobLoading ? <LoadingSpinner size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminConfigCommands;